import React, { useEffect, useMemo, useState } from "react"
import Icon from "../../../components/AppIcon"
import { Card, CardContent } from "../../../components/ui/card"
import { listOrgCertificates } from "../../../utils/certHelpers"
import { listOrgLearners } from "../../../utils/learnerHelpers"

const StatCard = ({ label, value, icon }) => (
  <Card className="p-4 dark:bg-transparent">
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Icon name={icon} size={14} className="text-muted-foreground" />
    </div>
    <p className="text-2xl font-bold text-foreground mt-0.5">{value}</p>
  </Card>
)

const IntelligenceOutcomesPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [certificates, setCertificates] = useState([])
  const [learners, setLearners] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    const load = async () => {
      setLoading(true)
      const [{ certificates: certs }, { learners: learnerList }] = await Promise.all([
        listOrgCertificates(orgId),
        listOrgLearners(orgId),
      ])
      setCertificates(certs || [])
      setLearners(learnerList || [])
      setLoading(false)
    }
    load()
  }, [orgId])

  const skillCounts = useMemo(() => {
    const counts = {}
    certificates.forEach((c) => {
      c?.templateSnapshot?.skills?.forEach((skill) => {
        counts[skill] = (counts[skill] || 0) + 1
      })
    })
    return Object.entries(counts)
      .map(([skill, count]) => ({ skill, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [certificates])

  const learnersWithCredentials = learners.filter((l) => l.credentialsCount > 0).length
  const totalProjectsCompleted = certificates.filter(
    (c) => c?.templateSnapshot?.evidence?.requireProjectSubmission
  ).length

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading outcomes...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-xl">
        What learning actually produced — skills gained and credentials earned. Employment and
        career-progression outcomes aren't tracked yet, so they aren't shown here rather than
        estimated.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Credentials Earned" value={certificates.length} icon="Award" />
        <StatCard label="Learners with a Credential" value={learnersWithCredentials} icon="UserCheck" />
        <StatCard label="Distinct Skills Certified" value={skillCounts.length} icon="Sparkles" />
      </div>

      <Card className="dark:bg-transparent">
        <CardContent className="p-4">
          <h3 className="font-semibold text-sm text-foreground mb-3">Top Skills Gained</h3>
          {skillCounts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No skills recorded yet — add skills to your Credential Templates so they show up here
              once credentials are issued.
            </p>
          ) : (
            <div className="space-y-2">
              {skillCounts.map((s) => {
                const max = skillCounts[0]?.count || 1
                return (
                  <div key={s.skill} className="flex items-center gap-3">
                    <span className="text-sm text-foreground w-32 truncate shrink-0">{s.skill}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${(s.count / max) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right">{s.count}</span>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="p-4 dark:bg-transparent border-dashed">
        <div className="flex items-start gap-2.5">
          <Icon name="Info" size={15} className="text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            Employment outcomes, career progression, and further-education tracking aren't
            available — there's no data source for what happens to a learner after they leave
            Nesolearn. This would need either a post-completion survey flow or an external
            integration (LinkedIn, employer verification) to populate honestly.
          </p>
        </div>
      </Card>
    </div>
  )
}

export default IntelligenceOutcomesPanel